import PropTypes from "prop-types";
import { Fragment, memo, useRef, useState } from "react";
import LazyLoad from "react-lazyload";
import { photoPropTypes } from "app/utils/propTypes";
import { MEDIA_TYPE } from "app/constants";
import Video from "app/pages/.shared/Video/Video";
import BackgroundProgressiveImg from "app/pages/.shared/BackgroundProgressiveImg";
import "./photoCarousel.scss";
import { Swiper, SwiperSlide } from "swiper/react";
import { Keyboard, Pagination, FreeMode } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import classNames from "classnames";
import { RESOLUTION } from "./responsive/responsiveReducer";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import {
	PHOTO_GALLERY_NAME,
	PHOTO_INTERACTION_TYPE,
	PHOTO_SWIPE_DIRECTION,
} from "app/utils/analytics/photoInteractionAnalytics";
import { ArrowRightIcon, ArrowLeftIcon } from "app/icons/navigation";

const IMG_SIZES = {
	small: { width: 800, height: 460 },
	medium: { width: 1024, height: 500 },
	large: { width: 1024, height: 500 },
	xlarge: { width: 1024, height: 500 },
};

const PhotoCarousel = memo(
	({ photos = [], resolution, onThumbnailClick }) => {
		const { track } = useAnalytics();

		const [swiper, setSwiper] = useState(null);
		const thumbnailSwiperRef = useRef(null);
		const [activeIndex, setActiveIndex] = useState(0);
		const [loadIndex, setLoadIndex] = useState(0);
		const [isBeginning, setIsBeginning] = useState(true);
		const [isEnd, setIsEnd] = useState(false);

		const slidePrev = () => swiper?.slidePrev();
		const slideNext = () => swiper?.slideNext();

		const handleSlideChange = swiperInstance => {
			const previousImageIndex = activeIndex;
			const visibleImageIndex = swiperInstance.activeIndex;

			if (thumbnailSwiperRef.current) {
				thumbnailSwiperRef.current.slideTo(visibleImageIndex);
			}
			setActiveIndex(visibleImageIndex);
			setLoadIndex(visibleImageIndex);
			setIsBeginning(swiperInstance.isBeginning);
			setIsEnd(swiperInstance.isEnd);
			if (resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM) {
				const visiblePhoto = photos[visibleImageIndex];

				track("photos_carousel_mobile_swipe", {
					photo_interaction: {
						gallery_name: PHOTO_GALLERY_NAME,
						interaction_type: PHOTO_INTERACTION_TYPE.SWIPE,
						swipe_direction:
							visibleImageIndex > previousImageIndex
								? PHOTO_SWIPE_DIRECTION.LEFT
								: PHOTO_SWIPE_DIRECTION.RIGHT,
						previous_image_index: previousImageIndex + 1,
						visible_image_index: visibleImageIndex + 1,
						total_image_count: photos.length,
						image_url: visiblePhoto?.url,
					},
				});
			}
		};

		const handleThumbnailClick = swiperInstance => {
			if (typeof onThumbnailClick === "function") {
				onThumbnailClick();
			}
			if (swiper && swiperInstance.clickedIndex !== null) {
				swiper.slideTo(swiperInstance.clickedIndex);
				setActiveIndex(swiperInstance.clickedIndex);
			}
		};

		const handleThumbnailTransitionEnd = swiperInstance => {
			if (swiperInstance.realIndex >= loadIndex) {
				setLoadIndex(swiperInstance.realIndex);
			}
		};

		const photosNodes = photos.map((photo, index) => {
			if (index <= loadIndex + 8) {
				if (photo.type === MEDIA_TYPE.VIDEO) {
					return (
						<SwiperSlide key={index}>
							<LazyLoad once offset={300}>
								<Video
									url={photo.url}
									autoplay={photo.autoplay}
									videoType={photo.videoType}
								/>
							</LazyLoad>
						</SwiperSlide>
					);
				}

				return (
					<SwiperSlide key={index}>
						<div
							className={classNames(
								{ "photo-carousel-slide--highlight": index === activeIndex },
								"photo-carousel-slide"
							)}
						>
							<BackgroundProgressiveImg
								sizes={IMG_SIZES}
								thumbnail={photo.thumbnail}
								src={photo.url}
								alt={photo.alt}
								cloudinaryOptions={["c_fill"]}
								quality={index === 0 && "q_auto:best"}
								backgroundPosition={"center"}
							/>
						</div>
					</SwiperSlide>
				);
			}
			return <SwiperSlide key={index} />;
		});

		const photosNodesThumbnail = photos.map((photo, key) => {
			if (key <= loadIndex + 8) {
				if (photo.type === MEDIA_TYPE.VIDEO) {
					return (
						<SwiperSlide key={key} className="thumbnail-video">
							<div className="thumbnail-video-overlay" />
							<LazyLoad once offset={300}>
								<Video
									url={photo.url}
									autoplay={photo.autoplay}
									videoType={photo.videoType}
								/>
							</LazyLoad>
						</SwiperSlide>
					);
				}

				return (
					<SwiperSlide key={key}>
						<div
							className={classNames(
								{ "photo-carousel-slide--highlight": key === activeIndex },
								"photo-carousel-slide"
							)}
						>
							<BackgroundProgressiveImg
								sizes={IMG_SIZES}
								thumbnail={photo.thumbnail}
								src={photo.url}
								alt={photo.alt}
								backgroundPosition={"center"}
							/>
						</div>
					</SwiperSlide>
				);
			}
			return <SwiperSlide key={key} />;
		});

		return (
			<Fragment>
				<div className="photo-carousel">
					<Swiper
						onSwiper={setSwiper}
						modules={[Pagination, Keyboard]}
						pagination={{ type: "fraction" }}
						keyboard={{ enabled: true }}
						onSlideChange={handleSlideChange}
					>
						{photosNodes}
					</Swiper>
					<div className="navlogs__carousel__button navlogs__carousel__button--next">
						{!isEnd && swiper && (
							<ArrowRightIcon
								className="photo-carousel-navigation icon icon-navigation icon-navigation-next flip-if-rlt"
								onClick={slideNext}
								data-testid="carousel-next-button"
							/>
						)}
					</div>
					<div className="navlogs__carousel__button navlogs__carousel__button--previous">
						{!isBeginning && swiper && (
							<ArrowLeftIcon
								className="photo-carousel-navigation icon icon-navigation icon-navigation-previous flip-if-rlt"
								onClick={slidePrev}
								data-testid="carousel-previous-button"
							/>
						)}
					</div>
				</div>
				<div className="photo-carousel-thumbnail">
					<Swiper
						onSwiper={swiper => {
							thumbnailSwiperRef.current = swiper;
						}}
						modules={[FreeMode]}
						spaceBetween={10}
						touchRatio={0.2}
						slideToClickedSlide={true}
						slidesPerView={7}
						freeMode={{ enabled: true, sticky: true }}
						preventClicksPropagation={false}
						onClick={handleThumbnailClick}
						onTransitionEnd={handleThumbnailTransitionEnd}
					>
						{photosNodesThumbnail}
					</Swiper>
				</div>
			</Fragment>
		);
	},
	(prevProps, nextProps) =>
		prevProps.photos === nextProps.photos &&
		prevProps.isSlideshowMode === nextProps.isSlideshowMode
);

PhotoCarousel.displayName = "PhotoCarousel";

PhotoCarousel.propTypes = {
	photos: PropTypes.arrayOf(photoPropTypes),
	isSlideshowMode: PropTypes.bool,
	resolution: PropTypes.string,
	onThumbnailClick: PropTypes.func,
};

export default PhotoCarousel;
